import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ApiError, Button, Card, Input, Label, StatusChip, fmtRelativeTime } from '@smena/ui';
import { Modal } from '@/components/Modal';
import { PageHeader } from '@/components/PageHeader';
import { QueryState } from '@/components/QueryState';
import { useMe } from '@/features/auth/hooks';
import {
  buildWelcomeMessage,
  createAdmin,
  deleteAdmin,
  fetchAdmins,
  resetAdminPassword,
  resetAdminTotp,
  setAdminActive,
  type AdminUserListItem,
  type CreatedAdmin,
} from '@/features/admins/api';

/** Разовый показ секрета после сброса - переиспользуем один компонент для пароля и для TOTP. */
type SecretResult =
  | { kind: 'password'; email: string; password: string }
  | { kind: 'totp'; email: string; totpSecret: string; qrDataUrl: string };

export function AdminsPage() {
  const [creating, setCreating] = useState(false);
  const [secret, setSecret] = useState<SecretResult | null>(null);
  const q = useQuery({ queryKey: ['admin-users'], queryFn: fetchAdmins, staleTime: 5_000 });

  return (
    <>
      <PageHeader
        title="Администраторы"
        subtitle="Команда вендора. Второй фактор обязателен: без TOTP вход невозможен."
        actions={<Button onClick={() => setCreating(true)}>Добавить администратора</Button>}
      />
      <QueryState
        isLoading={q.isLoading}
        error={q.error}
        isEmpty={q.data?.items.length === 0}
        emptyText="Администраторов пока нет"
      >
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="border-b border-line text-left text-xs uppercase tracking-wider text-muted">
              <tr>
                <th className="px-4 py-2 font-medium">Имя</th>
                <th className="px-4 py-2 font-medium">E-mail</th>
                <th className="px-4 py-2 font-medium">Статус</th>
                <th className="px-4 py-2 font-medium">Последний вход</th>
                <th className="px-4 py-2 font-medium">Заведён</th>
                <th className="px-4 py-2 text-right font-medium">Действия</th>
              </tr>
            </thead>
            <tbody>
              {q.data?.items.map((a: AdminUserListItem) => (
                <AdminRow key={a.id} admin={a} onSecret={setSecret} />
              ))}
            </tbody>
          </table>
        </Card>
      </QueryState>

      {creating ? <CreateAdminModal onClose={() => setCreating(false)} /> : null}
      {secret ? <SecretModal result={secret} onClose={() => setSecret(null)} /> : null}
    </>
  );
}

function AdminRow({
  admin,
  onSecret,
}: {
  admin: AdminUserListItem;
  onSecret: (r: SecretResult) => void;
}) {
  const qc = useQueryClient();
  const { data: me } = useMe();
  const isSelf = me?.id === admin.id;
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['admin-users'] });

  function run(action: () => Promise<unknown>, okText: string) {
    action()
      .then(() => {
        invalidate();
        toast.success(okText);
      })
      .catch((e) => toast.error(e instanceof ApiError ? e.message : 'Не удалось'));
  }

  return (
    <tr className="border-b border-line/60 last:border-0 hover:bg-panel2">
      <td className="px-4 py-2">
        {admin.name}
        {isSelf ? <span className="ml-2 text-[11px] text-muted">это вы</span> : null}
      </td>
      <td className="px-4 py-2 font-mono text-[13px]">{admin.email}</td>
      <td className="px-4 py-2">
        <StatusChip tone={admin.active ? 'green' : 'muted'}>
          {admin.active ? 'активен' : 'заблокирован'}
        </StatusChip>
      </td>
      <td className="px-4 py-2 text-muted">
        {admin.lastLoginAt ? fmtRelativeTime(admin.lastLoginAt) : 'ни разу'}
      </td>
      <td className="px-4 py-2 text-muted">{fmtRelativeTime(admin.createdAt)}</td>
      <td className="px-4 py-2">
        <div className="flex flex-wrap justify-end gap-1.5">
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              run(
                () =>
                  resetAdminPassword(admin.id).then((r) =>
                    onSecret({ kind: 'password', email: r.user.email, password: r.password }),
                  ),
                'Пароль сброшен, сессии закрыты',
              )
            }
          >
            Сбросить пароль
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              if (!confirm(`Перевыпустить второй фактор для ${admin.email}? Старый код перестанет работать.`)) return;
              run(
                () =>
                  resetAdminTotp(admin.id).then((r) =>
                    onSecret({
                      kind: 'totp',
                      email: r.user.email,
                      totpSecret: r.totpSecret,
                      qrDataUrl: r.qrDataUrl,
                    }),
                  ),
                'Второй фактор перевыпущен',
              );
            }}
          >
            Сбросить 2FA
          </Button>
          {/* Себя блокировать и удалять нельзя - кнопки прячем, сервер это же проверяет повторно. */}
          {isSelf ? null : (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  run(
                    () => setAdminActive(admin.id, !admin.active),
                    admin.active ? 'Администратор заблокирован' : 'Администратор разблокирован',
                  )
                }
              >
                {admin.active ? 'Заблокировать' : 'Разблокировать'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-destructive"
                onClick={() => {
                  if (!confirm(`Удалить ${admin.email}? Действие необратимо, войти он больше не сможет.`)) return;
                  run(() => deleteAdmin(admin.id), 'Администратор удалён');
                }}
              >
                Удалить
              </Button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

/** Пароль и TOTP после сброса тоже показываются РОВНО один раз - та же логика, что при создании. */
function SecretModal({ result, onClose }: { result: SecretResult; onClose: () => void }) {
  const isPw = result.kind === 'password';
  return (
    <Modal title={isPw ? 'Новый пароль' : 'Новый второй фактор'} onClose={onClose}>
      <div className="flex flex-col gap-4">
        <div className="rounded-md border border-amber/40 bg-amber/10 px-3 py-2 text-xs text-amber">
          Показывается <b>один раз</b>. Все сессии этого администратора закрыты - он войдёт заново
          с новыми данными.
        </div>

        {result.kind === 'totp' ? (
          <div className="flex flex-col items-center gap-2">
            {/* Белая подложка обязательна: сканеры не читают QR на тёмном фоне. */}
            <img
              src={result.qrDataUrl}
              alt={`QR второго фактора для ${result.email}`}
              className="size-52 rounded-md bg-white p-2"
            />
            <p className="text-center text-xs text-muted">
              Удалите старую запись в аутентификаторе и отсканируйте новый QR
            </p>
          </div>
        ) : null}

        <Row label="Логин" value={result.email} />
        {result.kind === 'password' ? (
          <Row label="Новый пароль" value={result.password} accent />
        ) : (
          <Row label="Новый ключ TOTP" value={result.totpSecret} mono />
        )}

        <div className="flex justify-end">
          <Button onClick={onClose}>Готово</Button>
        </div>
      </div>
    </Modal>
  );
}

function CreateAdminModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [created, setCreated] = useState<CreatedAdmin | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: createAdmin,
    onSuccess: (r) => {
      setCreated(r);
      void qc.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await mut.mutateAsync({ email, name });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось создать администратора');
    }
  }

  if (created) return <CreatedAdminView created={created} onClose={onClose} />;

  return (
    <Modal title="Новый администратор" onClose={onClose}>
      <form onSubmit={submit} className="flex flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="admin-name">Имя</Label>
          <Input
            id="admin-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Артём"
            required
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="admin-email">E-mail (он же логин)</Label>
          <Input
            id="admin-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@smenatech.ru"
            required
          />
        </div>
        <p className="text-xs text-muted">
          Пароль и ключ для аутентификатора сгенерируются автоматически и будут показаны один раз.
        </p>
        {error ? <p className="text-xs text-destructive">{error}</p> : null}
        <div className="mt-1 flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" disabled={mut.isPending}>
            {mut.isPending ? 'Создаём…' : 'Создать'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Экран «показывается один раз». Три способа забрать доступ, потому что сценарии разные:
 * QR - отсканировать телефоном тут же; ключ текстом - вбить в десктопный менеджер;
 * готовое сообщение - отправить человеку, которого нет рядом.
 */
function CreatedAdminView({ created, onClose }: { created: CreatedAdmin; onClose: () => void }) {
  // Хост берём из текущего origin: на стенде это admin.dev.smenatech.ru, локально - localhost:8789.
  const loginUrl = `${window.location.origin}/login`;
  const message = buildWelcomeMessage(created, loginUrl);

  return (
    <Modal title="Администратор создан" onClose={onClose}>
      <div className="flex max-h-[70vh] flex-col gap-4 overflow-y-auto">
        <div className="rounded-md border border-amber/40 bg-amber/10 px-3 py-2 text-xs text-amber">
          Пароль и ключ показываются <b>один раз</b>. В базе только хеш пароля - восстановить или
          показать повторно нельзя, забыли - заводите заново.
        </div>

        <div className="flex flex-col items-center gap-2">
          {/* Белая подложка обязательна: сканеры не читают QR на тёмном фоне. */}
          <img
            src={created.qrDataUrl}
            alt={`QR для настройки аутентификатора ${created.user.email}`}
            className="size-52 rounded-md bg-white p-2"
          />
          <p className="text-center text-xs text-muted">
            Аутентификатор → «Добавить» → сканировать QR
          </p>
        </div>

        <Row label="Адрес" value={loginUrl} />
        <Row label="Логин" value={created.user.email} />
        <Row label="Пароль" value={created.password} accent />
        <Row label="Ключ TOTP (если QR не сканируется)" value={created.totpSecret} mono />

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="welcome-msg">Готовое сообщение</Label>
          <textarea
            id="welcome-msg"
            readOnly
            value={message}
            rows={9}
            className="w-full rounded-md border border-line bg-panel2 p-3 font-mono text-[12px] leading-relaxed text-text"
          />
          <Button
            type="button"
            variant="secondary"
            className="self-start"
            onClick={() => {
              void navigator.clipboard.writeText(message);
              toast.success('Сообщение скопировано');
            }}
          >
            Скопировать сообщение
          </Button>
        </div>

        <div className="flex justify-end">
          <Button onClick={onClose}>Готово</Button>
        </div>
      </div>
    </Modal>
  );
}

function Row({
  label,
  value,
  accent,
  mono,
}: {
  label: string;
  value: string;
  accent?: boolean;
  mono?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-md border border-line bg-panel2 px-3 py-2">
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wider text-muted">{label}</div>
        <div
          className={[
            'truncate text-sm',
            mono || accent ? 'font-mono' : '',
            accent ? 'text-accent-fg' : '',
          ].join(' ')}
        >
          {value}
        </div>
      </div>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => {
          void navigator.clipboard.writeText(value);
          toast.success('Скопировано');
        }}
      >
        Копировать
      </Button>
    </div>
  );
}
